/**
 * 登录态存储（按浏览器 Tab 隔离）：优先使用 sessionStorage
 * 兼容旧逻辑：若 sessionStorage 为空，则回退到 localStorage（便于平滑升级）
 */
const KEYS = {
  token: 'token',
  account: 'account',
  userRole: 'userRole',
  /** 与 t_role.role_code 一致；会员账号用登录接口 roleCode，平台账号可与 userRole 相同 */
  roleCode: 'roleCode',
  /** 登录接口返回的菜单 key 列表 JSON，如 ["dashboard","org"] */
  allowedMenus: 'allowedMenus'
};

export function getToken() {
  return sessionStorage.getItem(KEYS.token) || localStorage.getItem(KEYS.token) || '';
}

export function setToken(token) {
  if (token) sessionStorage.setItem(KEYS.token, token);
  else sessionStorage.removeItem(KEYS.token);
}

export function getAccount() {
  return sessionStorage.getItem(KEYS.account) || localStorage.getItem(KEYS.account) || '';
}

export function setAccount(account) {
  if (account) sessionStorage.setItem(KEYS.account, account);
  else sessionStorage.removeItem(KEYS.account);
}

export function getUserRole() {
  return sessionStorage.getItem(KEYS.userRole) || localStorage.getItem(KEYS.userRole) || '';
}

export function setUserRole(role) {
  if (role) sessionStorage.setItem(KEYS.userRole, role);
  else sessionStorage.removeItem(KEYS.userRole);
}

export function getRoleCode() {
  return sessionStorage.getItem(KEYS.roleCode) || localStorage.getItem(KEYS.roleCode) || '';
}

export function setRoleCode(code) {
  const s = code != null ? String(code).trim() : '';
  if (s) {
    sessionStorage.setItem(KEYS.roleCode, s);
  } else {
    sessionStorage.removeItem(KEYS.roleCode);
  }
}

export function setAllowedMenus(menus) {
  if (menus && Array.isArray(menus)) {
    sessionStorage.setItem(KEYS.allowedMenus, JSON.stringify(menus));
  } else {
    sessionStorage.removeItem(KEYS.allowedMenus);
  }
}

export function getAllowedMenus() {
  const s = sessionStorage.getItem(KEYS.allowedMenus);
  if (!s) return null;
  try {
    const p = JSON.parse(s);
    return Array.isArray(p) ? p : null;
  } catch (e) {
    return null;
  }
}

export function clearSessionAuth() {
  sessionStorage.removeItem(KEYS.token);
  sessionStorage.removeItem(KEYS.account);
  sessionStorage.removeItem(KEYS.userRole);
  sessionStorage.removeItem(KEYS.roleCode);
  sessionStorage.removeItem(KEYS.allowedMenus);
}

/**
 * 机构 / 会员 / 学生管理列表：批量删除与行内「删除」仅系统管理员或超级管理员可见。
 * - 系统管理员：roleCode / userRole 为 SYSTEM_ADMIN（t_role）
 * - 超级管理员：平台账号 roleCode / userRole 为 admin；兼容登录名为 admin
 */
export function canShowOrgStaffStudentListDelete() {
  const rc = (getRoleCode() || '').trim();
  const ur = (getUserRole() || '').trim();
  const acc = (getAccount() || '').trim().toLowerCase();
  if (rc === 'SYSTEM_ADMIN' || ur === 'SYSTEM_ADMIN') return true;
  if (rc === 'admin' || ur === 'admin') return true;
  if (acc === 'admin') return true;
  return false;
}

/**
 * 电子地图「校区标注」Tab：默认不展示；仅超级管理员 admin、系统管理员 SYSTEM_ADMIN、
 * 商务人员 BUSINESS、运营人员 OPERATION 可见。
 * 与 canShowOrgStaffStudentListDelete 中 admin / SYSTEM_ADMIN 判定方式一致，并增加 BUSINESS / OPERATION。
 */
export function canShowElectronicMapCampusMarkingTab() {
  const rc = (getRoleCode() || '').trim();
  const ur = (getUserRole() || '').trim();
  const acc = (getAccount() || '').trim().toLowerCase();
  if (rc === 'BUSINESS' || ur === 'BUSINESS') return true;
  if (rc === 'OPERATION' || ur === 'OPERATION') return true;
  if (rc === 'SYSTEM_ADMIN' || ur === 'SYSTEM_ADMIN') return true;
  if (rc === 'admin' || ur === 'admin') return true;
  if (acc === 'admin') return true;
  return false;
}

/**
 * 解析 JWT payload（标准 JSON 为 UTF-8 编码）。
 * 勿使用 atob 结果直接 JSON.parse：中文等多字节字符会乱码。
 * @param {string} token
 * @returns {object|null}
 */
export function decodeJwtPayload(token) {
  if (!token || typeof token !== 'string') return null;
  const parts = token.split('.');
  if (parts.length < 2) return null;
  try {
    let b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    while (b64.length % 4 !== 0) b64 += '=';
    const binary = atob(b64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    const jsonStr = new TextDecoder('utf-8').decode(bytes);
    return JSON.parse(jsonStr);
  } catch (e) {
    return null;
  }
}

